import { useState } from 'react';
import { PageHeader } from '../components/Layout';
import { ApolloImportManager } from '../components/ApolloImport';
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Select,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';
import {
  api,
  type IntegrationStatus,
  type IntentConfigField,
  type IntentSource,
  type IntentSyncResult,
  type KeywordTier,
  type User,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

interface KeyStatus {
  integrations: IntegrationStatus[];
  server_claude_key: boolean;
  google_oauth_configured: boolean;
}

export function Settings() {
  const { user } = useAuth();
  const keys = useApi<KeyStatus>('/api/keys/status');
  const sources = useApi<{ sources: IntentSource[] }>('/api/intent/sources');
  const users = useApi<{ users: User[] }>('/api/users');
  const [syncResult, setSyncResult] = useState<IntentSyncResult | null>(null);
  const syncState = useApi<{ sync_state: Array<{ key: string; value: Record<string, unknown>; updated_at: string }> }>(
    '/api/sync-state',
  );
  const [showInvite, setShowInvite] = useState(false);

  const gmail = keys.data?.integrations.find((i) => i.provider === 'gmail');
  const anthropic = keys.data?.integrations.find((i) => i.provider === 'anthropic');
  const lastSync = syncState.data?.sync_state.find((s) => s.key === 'gmail_last_sync');

  const connectGmail = useAction(async () => {
    const { url } = await api.get<{ url: string }>('/api/oauth/google/start');
    window.location.href = url;
  });

  const disconnect = useAction(async (provider: string) => {
    await api.del(`/api/keys/${provider}`);
    keys.reload();
  });

  const syncNow = useAction(async () => {
    setSyncResult(await api.post<IntentSyncResult>('/api/intent/sync', {}));
    sources.reload();
  });

  if (keys.loading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Settings"
        description="Integrations, keys, and who has access."
        helpHref="settings"
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Claude">
          <p className="text-sm text-ink-600">
            Powers drafting, reply analysis, and win/loss suggestions. Keys are encrypted with
            AES-256-GCM before they touch the database and are never returned by the API.
          </p>

          <div className="mt-3 flex items-center gap-2">
            {anthropic?.connected ? (
              <>
                <Badge tone="green">Connected</Badge>
                {typeof anthropic.metadata.hint === 'string' && (
                  <span className="font-mono text-xs text-ink-500">{anthropic.metadata.hint}</span>
                )}
              </>
            ) : keys.data?.server_claude_key ? (
              <Badge tone="blue" title="No personal key saved, so requests fall back to the key configured for the whole server.">
                Using the server key
              </Badge>
            ) : (
              <Badge tone="amber">Not configured</Badge>
            )}
          </div>

          <ClaudeKeyForm onSaved={keys.reload} />

          {anthropic?.connected && (
            <Button
              size="sm"
              variant="danger"
              className="mt-2"
              loading={disconnect.pending}
              onClick={() => void disconnect.run('anthropic')}
            >
              Remove key
            </Button>
          )}
        </Card>

        <Card title="Gmail">
          <p className="text-sm text-ink-600">
            Sends from your own mailbox and polls your outreach threads for replies. The app never
            modifies your mail — it sends and reads, nothing else.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {gmail?.connected ? (
              <>
                <Badge tone="green">Connected</Badge>
                {typeof gmail.metadata.email === 'string' && (
                  <span className="text-sm text-ink-700">{gmail.metadata.email}</span>
                )}
              </>
            ) : (
              <Badge tone="amber">Not connected</Badge>
            )}
          </div>

          {!keys.data?.google_oauth_configured && (
            <div className="mt-3">
              <ErrorNote message="Set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET on the server to enable Gmail." />
            </div>
          )}

          {lastSync && (
            <p className="mt-2 text-xs text-ink-500">
              Last sync {dateTime(lastSync.updated_at)} · {String(lastSync.value.repliesFound ?? 0)} replies found
              across {String(lastSync.value.threadsChecked ?? 0)} threads
            </p>
          )}

          {connectGmail.error && <div className="mt-2"><ErrorNote message={connectGmail.error} /></div>}

          <div className="mt-3 flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!keys.data?.google_oauth_configured}
              loading={connectGmail.pending}
              onClick={() => void connectGmail.run()}
            >
              {gmail?.connected ? 'Reconnect' : 'Connect Gmail'}
            </Button>
            {gmail?.connected && (
              <Button
                size="sm"
                variant="danger"
                loading={disconnect.pending}
                onClick={() => void disconnect.run('gmail')}
              >
                Disconnect
              </Button>
            )}
          </div>
        </Card>

        <Card title="LinkedIn">
          <p className="text-sm text-ink-600">
            LinkedIn does not offer DM sending, profile scraping, or profile-view data to standard
            developer accounts, and scraping the site breaches their terms of service. The app treats
            LinkedIn as assisted-manual: Claude drafts the message and tracks the touch, you paste and
            send it yourself.
          </p>
          <p className="mt-2 text-xs text-ink-500">
            If your organisation is approved for LinkedIn's Messaging API, only one function needs to
            change server-side (<span className="font-mono">services/linkedin.ts → sendDM</span>).
          </p>
        </Card>

        <Card
          title="Intent signals"
          className="lg:col-span-2"
          action={
            user?.role === 'admin' && (
              <Button size="sm" loading={syncNow.pending} onClick={() => void syncNow.run()}>
                Sync now
              </Button>
            )
          }
        >
          <p className="text-sm text-ink-600">
            Buying signals from third-party providers: who is hiring for roles you serve, who
            changed jobs, who engaged with relevant content. Signals are scored per company and
            decay over time, so the ranking reflects who is in-market now. Keys are encrypted the
            same way as the Claude key above.
          </p>

          {syncNow.error && <div className="mt-3"><ErrorNote message={syncNow.error} /></div>}
          {syncResult && <SyncSummary result={syncResult} />}

          {sources.loading ? (
            <div className="mt-4"><Loading /></div>
          ) : (
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              {(sources.data?.sources ?? []).map((source) => (
                <IntentSourceCard
                  key={source.provider}
                  source={source}
                  status={keys.data?.integrations.find((i) => i.provider === source.provider)}
                  canEditConfig={user?.role === 'admin'}
                  onChanged={() => {
                    keys.reload();
                    sources.reload();
                  }}
                />
              ))}
            </div>
          )}

          <p className="mt-4 text-xs text-ink-500">
            RB2B pushes website visitors to{' '}
            <span className="font-mono">/api/intent/webhooks/rb2b</span> instead of being polled, so
            it needs no key or configuration here — paste that URL into RB2B's webhook settings.
          </p>
        </Card>

        <Card
          title="Apollo list imports"
          className="lg:col-span-2"
        >
          <p className="text-sm text-ink-600 mb-4">
            Search Apollo and automatically import contacts as prospects. Configure one-time imports
            or set up recurring syncs to stay in sync with your Apollo searches.
          </p>
          {keys.data?.integrations.find((i) => i.provider === 'apollo')?.connected ? (
            <ApolloImportManager />
          ) : (
            <ErrorNote message="Apollo API key not configured. Set it up in the integrations section above." />
          )}
        </Card>

        <Card title="Data & privacy">
          <ul className="space-y-2 text-sm text-ink-600">
            <li>API keys and OAuth tokens are AES-256-GCM encrypted at rest.</li>
            <li>Passwords are bcrypt hashed; sessions are JWTs with a one-week expiry.</li>
            <li>
              Deleting a prospect with <span className="font-mono text-xs">?hard=true</span> cascades to
              every activity, reply, and outcome — the GDPR erasure path.
            </li>
            <li>Prospect data is exportable as CSV from the prospects page.</li>
          </ul>
        </Card>
      </div>

      <Card
        title="Team"
        className="mt-4"
        action={
          user?.role === 'admin' && (
            <Button size="sm" variant="primary" onClick={() => setShowInvite(true)}>
              Add member
            </Button>
          )
        }
        bodyClassName="p-0"
      >
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Prospects owned</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(users.data?.users ?? []).map((member) => (
              <tr key={member.id}>
                <Td className="font-medium">{member.full_name || '—'}</Td>
                <Td>{member.email}</Td>
                <Td>
                  <Badge tone={member.role === 'admin' ? 'blue' : 'neutral'}>{member.role}</Badge>
                </Td>
                <Td className="tabular-nums">{member.owned_prospects ?? 0}</Td>
                <Td>
                  <Badge tone={member.is_active ? 'green' : 'red'}>
                    {member.is_active ? 'active' : 'disabled'}
                  </Badge>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <InviteModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onDone={() => {
          setShowInvite(false);
          users.reload();
        }}
      />
    </>
  );
}

const PROVIDER_DOCS: Record<string, string> = {
  apollo: 'https://docs.apollo.io/reference/apollo-api',
  theirstack: 'https://api.theirstack.com/',
  trigify: 'https://www.trigify.io/',
};

/** Renders the outcome of a manual sync, including quietly skipped providers. */
function SyncSummary({ result }: { result: IntentSyncResult }) {
  return (
    <div className="mt-3 rounded-lg border border-ink-200 bg-ink-50 p-3 text-xs text-ink-700">
      <p>
        Fetched {result.synced} signals · {result.inserted} new · {result.duplicates} already seen
        {result.scored > 0 && ` · rescored ${result.scored} companies`}
      </p>
      {result.skipped.length > 0 && (
        <p className="mt-1 text-ink-500">Skipped (no key): {result.skipped.join(', ')}</p>
      )}
      {result.errors.length > 0 && (
        <ul className="mt-1 list-inside list-disc text-red-700">
          {result.errors.slice(0, 3).map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A textarea holding one entry per line, edited as a string[]. */
function LinesField({
  field,
  value,
  onChange,
  disabled,
}: {
  field: IntentConfigField;
  value: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}) {
  // Split on every keystroke rather than on blur, so the caller always holds
  // the committed value and Save never misses a half-typed last line.
  return (
    <Field label={field.label} hint={field.hint}>
      <Textarea
        rows={4}
        disabled={disabled}
        value={value.join('\n')}
        placeholder={field.placeholder}
        onChange={(event) =>
          onChange(
            event.target.value
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean),
          )
        }
      />
    </Field>
  );
}

/**
 * Repeating editor for keyword tiers: each carries a label, a strength, and its
 * own keyword list. Strength is what separates a title that means someone is
 * buying from one that merely correlates with it.
 */
function TiersField({
  field,
  value,
  onChange,
  disabled,
}: {
  field: IntentConfigField;
  value: KeywordTier[];
  onChange: (next: KeywordTier[]) => void;
  disabled: boolean;
}) {
  const update = (index: number, patch: Partial<KeywordTier>) =>
    onChange(value.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));

  return (
    <div>
      <p className="text-sm font-medium text-ink-800">{field.label}</p>
      <p className="mt-1 text-xs text-ink-500">{field.hint}</p>

      <div className="mt-3 space-y-3">
        {value.map((tier, index) => (
          <div key={index} className="rounded-md border border-ink-200 bg-ink-50/50 p-3">
            <div className="flex items-center gap-2">
              <Input
                aria-label="Tier name"
                className="flex-1"
                disabled={disabled}
                value={tier.label}
                placeholder="Tier name"
                onChange={(event) => update(index, { label: event.target.value })}
              />
              <Input
                aria-label="Strength"
                type="number"
                min={1}
                max={100}
                className="w-20"
                disabled={disabled}
                value={String(tier.strength)}
                onChange={(event) => update(index, { strength: Number(event.target.value) })}
              />
              {!disabled && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              )}
            </div>
            <Textarea
              aria-label={`${tier.label} keywords`}
              className="mt-2"
              rows={4}
              disabled={disabled}
              value={tier.keywords.join('\n')}
              placeholder="One job title keyword per line"
              onChange={(event) =>
                update(index, {
                  keywords: event.target.value
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
        ))}
      </div>

      {!disabled && (
        <Button
          size="sm"
          className="mt-2"
          onClick={() => onChange([...value, { label: '', strength: 50, keywords: [] }])}
        >
          Add tier
        </Button>
      )}
    </div>
  );
}

function IntentSourceCard({
  source,
  status,
  canEditConfig,
  onChanged,
}: {
  source: IntentSource;
  status: IntegrationStatus | undefined;
  canEditConfig: boolean;
  onChanged: () => void;
}) {
  const [key, setKey] = useState('');
  const [config, setConfig] = useState<Record<string, unknown>>(source.config);
  const [enabled, setEnabled] = useState(source.enabled);

  const saveKey = useAction(async () => {
    await api.post('/api/keys/store', { provider: source.provider, key });
    setKey('');
    onChanged();
  });

  const removeKey = useAction(async () => {
    await api.del(`/api/keys/${source.provider}`);
    onChanged();
  });

  const saveConfig = useAction(async () => {
    await api.put(`/api/intent/sources/${source.provider}`, { enabled, config });
    onChanged();
  });

  // A provider only produces signals with a key, an enabled source, and every
  // required field filled in. Say which piece is missing rather than leaving a
  // card that looks configured but silently returns nothing. Tiers count as
  // empty until one actually carries a keyword — labelled but wordless tiers
  // would otherwise read as configured while every sync skipped.
  const missing = source.requires.filter((name) => {
    const value = config[name];
    if (!Array.isArray(value) || value.length === 0) return true;
    if (source.fields.find((f) => f.name === name)?.type !== 'tiers') return false;
    return !(value as KeywordTier[]).some((tier) => tier?.keywords?.length > 0);
  });

  return (
    <div className="rounded-lg border border-ink-200 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink-900">{source.label}</span>
        <Badge tone={status?.connected ? 'green' : 'neutral'}>
          {status?.connected ? 'Key set' : 'No key'}
        </Badge>
      </div>

      <p className="mt-1 text-xs text-ink-600">{source.description}</p>

      {status?.connected && typeof status.metadata.hint === 'string' && (
        <p className="mt-2 font-mono text-xs text-ink-500">{status.metadata.hint}</p>
      )}

      <div className="mt-3 space-y-2">
        <Input
          type="password"
          value={key}
          onChange={(event) => setKey(event.target.value)}
          placeholder={`${source.label} API key`}
          autoComplete="off"
        />
        {saveKey.error && <ErrorNote message={saveKey.error} />}
        {removeKey.error && <ErrorNote message={removeKey.error} />}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            loading={saveKey.pending}
            disabled={key.trim().length < 8}
            onClick={() => void saveKey.run()}
          >
            {status?.connected ? 'Replace key' : 'Save key'}
          </Button>
          {status?.connected && (
            <Button
              size="sm"
              variant="danger"
              loading={removeKey.pending}
              onClick={() => void removeKey.run()}
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 border-t border-ink-100 pt-3">
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!canEditConfig}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          Include in scheduled syncs
        </label>

        {source.fields.map((field) =>
          field.type === 'tiers' ? (
            <div className="mt-3" key={field.name}>
              <TiersField
                field={field}
                disabled={!canEditConfig}
                value={
                  Array.isArray(config[field.name]) ? (config[field.name] as KeywordTier[]) : []
                }
                onChange={(next) => setConfig((prev) => ({ ...prev, [field.name]: next }))}
              />
            </div>
          ) : field.type === 'lines' ? (
            <div className="mt-3" key={field.name}>
              <LinesField
                field={field}
                disabled={!canEditConfig}
                value={Array.isArray(config[field.name]) ? (config[field.name] as string[]) : []}
                onChange={(next) => setConfig((prev) => ({ ...prev, [field.name]: next }))}
              />
            </div>
          ) : (
            <div className="mt-3" key={field.name}>
              <Field label={field.label} hint={field.hint}>
                <Input
                  type="number"
                  min={field.min}
                  max={field.max}
                  disabled={!canEditConfig}
                  value={String(config[field.name] ?? '')}
                  onChange={(event) =>
                    setConfig((prev) => ({ ...prev, [field.name]: Number(event.target.value) }))
                  }
                />
              </Field>
            </div>
          ),
        )}

        {enabled && missing.length > 0 && (
          <p className="mt-2 text-xs text-amber-700">
            Add at least one entry to {missing.join(' and ')} — this provider returns nothing until
            you do.
          </p>
        )}

        {saveConfig.error && <div className="mt-2"><ErrorNote message={saveConfig.error} /></div>}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={!canEditConfig}
            loading={saveConfig.pending}
            onClick={() => void saveConfig.run()}
          >
            Save settings
          </Button>
          <a
            href={PROVIDER_DOCS[source.provider]}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-ink-500 underline"
          >
            Docs
          </a>
        </div>

        {!canEditConfig && (
          <p className="mt-2 text-xs text-ink-500">Only admins can change these settings.</p>
        )}

        {source.last_synced_at && (
          <p className="mt-2 text-xs text-ink-500">Last synced {dateTime(source.last_synced_at)}</p>
        )}
      </div>
    </div>
  );
}

function ClaudeKeyForm({ onSaved }: { onSaved: () => void }) {
  const [key, setKey] = useState('');

  const save = useAction(async () => {
    await api.post('/api/keys/store', { provider: 'anthropic', key });
    setKey('');
    onSaved();
  });

  return (
    <div className="mt-3 space-y-2">
      <Field label="Claude API key" hint="Paste a new key to replace the stored one.">
        <Input
          type="password"
          value={key}
          onChange={(event) => setKey(event.target.value)}
          placeholder="sk-ant-…"
          autoComplete="off"
        />
      </Field>
      {save.error && <ErrorNote message={save.error} />}
      <Button
        size="sm"
        variant="primary"
        loading={save.pending}
        disabled={key.trim().length < 8}
        onClick={() => void save.run()}
      >
        Save key
      </Button>
    </div>
  );
}

function InviteModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('member');

  const create = useAction(async () => {
    await api.post('/api/users', { email, full_name: fullName, password, role });
    setEmail('');
    setFullName('');
    setPassword('');
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title="Add a team member">
      <div className="space-y-3">
        <p className="text-sm text-ink-600">
          Set an initial password and share it with them — they can change it after signing in.
        </p>
        <Field label="Full name">
          <Input value={fullName} onChange={(event) => setFullName(event.target.value)} />
        </Field>
        <Field label="Email">
          <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Initial password" hint="At least 10 characters.">
          <Input
            type="text"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="off"
          />
        </Field>
        <Field
          label="Role"
          tip="Only admins can approve drafts, send or schedule messages, and manage integrations and team members."
        >
          <Select value={role} onChange={(event) => setRole(event.target.value)}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </Select>
        </Field>

        {create.error && <ErrorNote message={create.error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.pending} onClick={() => void create.run()}>
            Add member
          </Button>
        </div>
      </div>
    </Modal>
  );
}
